import { forwardRef, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { Input, InputProps } from './input';

// could be extended in the future if needed
// but at the moment, it is using the exact same props as the Input component
export type AutoResizeInputProps = InputProps;

const AutoResizeInput = forwardRef<HTMLInputElement, AutoResizeInputProps>(({ className, value = '', placeholder = '', containerClassName, ...props }, ref) => {
  const [width, setWidth] = useState<number | undefined>(undefined);
  const measureRef = useRef<HTMLSpanElement>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useLayoutEffect(() => {
    if (!measureRef.current || !inputRef.current) return;

    if (!value && !placeholder) {
      setWidth(12);
      return;
    }

    const textToMeasure = String(value || placeholder || 'W');
    measureRef.current.textContent = textToMeasure;

    const inputStyles = window.getComputedStyle(inputRef.current);

    Object.assign(measureRef.current.style, {
      font: inputStyles.font,
      fontSize: inputStyles.fontSize,
      fontFamily: inputStyles.fontFamily,
      fontWeight: inputStyles.fontWeight,
      letterSpacing: inputStyles.letterSpacing,
      padding: '0',
      margin: '0',
      border: 'none',
    });

    const textWidth = measureRef.current.offsetWidth;

    const paddingLeft = parseInt(inputStyles.paddingLeft, 10) || 0;
    const paddingRight = parseInt(inputStyles.paddingRight, 10) || 0;
    const borderLeft = parseInt(inputStyles.borderLeftWidth, 10) || 0;
    const borderRight = parseInt(inputStyles.borderRightWidth, 10) || 0;

    const calculatedWidth = textWidth + paddingLeft + paddingRight + borderLeft + borderRight;

    setWidth(calculatedWidth);
  }, [value, placeholder]);

  const handleRef = (element: HTMLInputElement | null) => {
    inputRef.current = element;
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
  };

  return (
    <div className={cn('relative flex w-auto items-center', containerClassName)}>
      {/* Hidden measuring span for autoSize */}
      <span ref={measureRef} className="invisible absolute whitespace-pre" style={{ pointerEvents: 'none' }} />

      <Input
        value={value}
        placeholder={placeholder}
        className={cn('w-auto', className)}
        containerClassName="w-auto"
        style={width ? { width: `${width}px` } : undefined}
        ref={handleRef}
        {...props}
      />
    </div>
  );
});

AutoResizeInput.displayName = 'AutoResizeInput';

export { AutoResizeInput };
